/**
 * Out-of-combat inventory drawer (ADR-0015 §1, §12; reframed by ADR-0020 §4c).
 *
 * A right-anchored slide-in **drawer** layered over a dimmed backdrop — not a
 * full-screen swap (ADR-0020 §4c "stop feeling like a website"). Mounted as a
 * DOM overlay above Pixi, separate from the combat overlay. Edits a character's
 * single loadout grid (3×3, combat-active) — the only card store (ADR-0022;
 * stash retired). The loadout is the entire collection (ADR-0015 §1); there is
 * no tray. Editing means moving cards between cells and rotating them. A
 * selected-card preview (display-mode Card) carries an Equip (auto-fit)
 * shortcut.
 *
 * NO active-synergies and NO build-totals panels — cut, not stubbed (ADR-0020
 * §4d): they are not real mechanics.
 *
 * Interaction (ADR-0011 §3 extended to card placement):
 *   - Drag a card onto a target cell → places it there.
 *   - Click-then-click fallback: click a card to pick it up, then click a cell.
 *   - Rotation on placement (ADR-0015 §1): with a card picked up, the Rotate
 *     control (or the `r` key) toggles the pending orientation; the placement is
 *     committed when the card is dropped / the target cell is clicked.
 *   - Equip (preview panel): first-fit the held card into a free loadout cell
 *     without hunting for one.
 * Every drop is gated by `canPlace` (via `inventoryEdit.moveCard`); illegal
 * targets are not highlighted and rejected drops flash.
 *
 * Reads / writes through a feed: `onChange(inventory)` fires after every
 * successful move; the host owns persistence.
 *
 * Acquisition mode (HEX-149): mounting with an `offer` turns the drawer into the
 * unified gain surface — the incoming card/die shows beside the loadout, the
 * player places it into a chosen cell (or Equips it), destroying cards to make
 * room, or declines it; `offer.onResolve` fires once with `placed` / `declined`.
 *
 * Contract (load-bearing — the shop opens this, HEX-106): the constructor and
 * `mount(container, { inventory, onChange?, onClose?, offer? })` signature are
 * stable. Only what they render changed (screen → drawer).
 */

import { isDieCard } from '../cardEngine';
import { PLAIN_D6 } from '../dice';
import { canPlace, footprint } from '../grid';
import type { Card, GridPlacement, Inventory } from '../types';
import { renderCardFace } from './cardFace';
import { CardHoverPanel, buildLegend } from './cardHoverPanel';
import { renderDieChip, renderGridDieChip } from './dieTray';
import { allCells, cardGridArea } from './gridLayout';
import {
  type GridName,
  findPlacement,
  firstFitOnGrid,
  moveCard,
  placeCardAt,
  removeCard,
} from './inventoryEdit';

/**
 * An incoming card/die offered alongside the loadout (HEX-149). When mounted with
 * an offer the editor becomes a unified **acquisition** surface: the offer shows
 * beside the loadout, the player drags it into a chosen cell (or Equips it to the
 * first fit), destroying cards to make room, or declines it. The offer resolves
 * exactly once — `placed` (the item now sits in the loadout) or `declined`
 * (forgone). The host persists + tears the editor down on resolve.
 */
export interface AcquisitionOffer {
  /** The incoming item — a card, or a die already wrapped via `dieCard`. */
  readonly item: Card;
  /** Label for the decline action (e.g. "Take 12 gold", "Leave it", "Forgo"). */
  readonly declineLabel: string;
  /** Fired once, when the acquisition resolves. */
  readonly onResolve: (outcome: 'placed' | 'declined') => void;
}

export interface InventoryEditorMountOptions {
  inventory: Inventory;
  onChange?: (inventory: Inventory) => void;
  onClose?: () => void;
  /** When set, the editor presents this incoming item beside the loadout for
   * place-or-decline curation (HEX-149). Omit for a plain inventory edit. */
  offer?: AcquisitionOffer;
}

const DRAG_MIME = 'application/x-hex6-card';
const DATA_CARD_ID = 'data-card-id';

export class InventoryEditor {
  private root: HTMLDivElement | null = null;
  private inventory: Inventory;
  private onChange: ((inventory: Inventory) => void) | null = null;
  private onClose: (() => void) | null = null;

  private selectedCardId: string | null = null;
  private pendingRotated = false;
  /** True between a card's `dragstart` and its drop/`dragend` — lets an off-grid
   * drop clear the pending selection cleanly (a click-pick leaves it false). */
  private dragActive = false;

  /** The incoming acquisition offer (HEX-149), or null for a plain edit. */
  private offer: AcquisitionOffer | null = null;
  /** True while the offer item is the thing "in hand" (awaiting a cell). Cleared
   * when the player picks up a grid card to curate, re-set by re-grabbing the
   * offer chip. */
  private offerHeld = false;
  /** Guards `onResolve` against a double fire (place + a trailing back-out). */
  private offerSettled = false;

  private loadoutMount: HTMLDivElement | null = null;
  private previewMount: HTMLDivElement | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private readonly hoverPanel = new CardHoverPanel();

  constructor(inventory: Inventory) {
    this.inventory = inventory;
  }

  mount(container: HTMLElement, options: InventoryEditorMountOptions): void {
    if (this.root) return;
    this.inventory = options.inventory;
    this.onChange = options.onChange ?? null;
    this.onClose = options.onClose ?? null;
    this.offer = options.offer ?? null;
    this.offerHeld = this.offer !== null;
    this.offerSettled = false;

    const root = document.createElement('div');
    root.className = 'hex6-inv-editor';
    if (this.offer) root.classList.add('hex6-inv-editor--acquire');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', this.offer ? 'Acquire item' : 'Inventory editor');

    // Dimmed backdrop — clicking it closes the drawer / declines the offer.
    const backdrop = document.createElement('div');
    backdrop.className = 'hex6-inv-editor__backdrop';
    backdrop.addEventListener('click', () => this.requestClose());

    const drawer = document.createElement('div');
    drawer.className = 'hex6-inv-editor__drawer';

    const grip = document.createElement('span');
    grip.className = 'hex6-inv-editor__grip';
    grip.setAttribute('aria-hidden', 'true');

    drawer.append(grip, this.buildHeader(), this.buildBody(), this.buildFooter());
    root.append(backdrop, drawer);
    container.appendChild(root);
    this.root = root;

    this.keyHandler = (e) => this.onKey(e);
    document.addEventListener('keydown', this.keyHandler);

    this.render();
  }

  destroy(): void {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    this.hoverPanel.destroy();
    this.root?.remove();
    this.root = null;
    this.loadoutMount = null;
    this.previewMount = null;
    this.onChange = null;
    this.onClose = null;
    this.selectedCardId = null;
    this.offer = null;
    this.offerHeld = false;
  }

  /** A close affordance (backdrop / header / Esc): declines an unplaced offer
   * (the item is forgone, HEX-149), else closes the plain editor. */
  private requestClose(): void {
    if (this.offer) {
      this.declineOffer();
      return;
    }
    this.onClose?.();
  }

  /** Test seam — current inventory. */
  getInventory(): Inventory {
    return this.inventory;
  }

  // ── building ──────────────────────────────────────────────────────────

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'hex6-inv-editor__header';

    const offering = this.offer !== null;
    const isDie = this.offer !== null && isDieCard(this.offer.item);

    const titles = document.createElement('div');
    titles.className = 'hex6-inv-editor__titles';
    const title = document.createElement('div');
    title.className = 'hex6-inv-editor__title';
    title.textContent = offering ? (isDie ? 'New die' : 'New card') : 'Loadout';
    const sub = document.createElement('div');
    sub.className = 'hex6-inv-editor__subtitle';
    sub.innerHTML = offering
      ? 'Drag it into a cell, or destroy a card to make room. <span class="hex6-inv-editor__key">R</span> rotates.'
      : 'Pack your deck — drag to fit, <span class="hex6-inv-editor__key">R</span> to rotate. No overlaps.';
    titles.append(title, sub);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hex6-inv-editor__close btn';
    close.dataset.action = 'close';
    close.textContent = offering ? 'Decline ✕' : 'Done ✕';
    close.addEventListener('click', () => this.requestClose());

    header.append(titles, close);
    return header;
  }

  private buildBody(): HTMLElement {
    const body = document.createElement('div');
    body.className = 'hex6-inv-editor__body';
    this.loadoutMount = this.buildGridColumn('loadout');
    this.previewMount = document.createElement('div');
    this.previewMount.className = 'hex6-inv-editor__preview';
    body.append(this.buildLoadoutColumn(), this.previewMount);
    return body;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'hex6-inv-editor__footer';
    footer.append(this.buildDestroyZone(), this.buildFooterLegend());
    return footer;
  }

  private buildFooterLegend(): HTMLElement {
    const legend = buildLegend();
    legend.classList.add('hex6-inv-editor__legend');
    return legend;
  }

  /** Drop-target that removes (destroys) the dropped card or die — the REMOVE
   * half of place-or-decline (HEX-147; ADR-0022 "curate via the drawer"). Drag a
   * placement here, or pick one up and click, to free its cell. Destruction is
   * permanent (run-scoped) so it is gated by a confirm. Cards AND dice. */
  private buildDestroyZone(): HTMLElement {
    const zone = document.createElement('div');
    zone.className = 'hex6-inv-editor__destroy';
    zone.dataset.role = 'destroy-zone';
    zone.innerHTML =
      '<span class="hex6-inv-editor__destroy-icon" aria-hidden="true">🗑</span>' +
      '<span class="hex6-inv-editor__destroy-label">Drag here to destroy</span>';
    zone.addEventListener('dragover', (e) => this.handleDestroyDragOver(e, zone));
    zone.addEventListener('dragleave', () =>
      zone.classList.remove('hex6-inv-editor__destroy--over'),
    );
    zone.addEventListener('drop', (e) => this.handleDestroyDrop(e, zone));
    // Click fallback (parity with click-then-click placement): with a card picked
    // up, clicking the zone destroys it.
    zone.addEventListener('click', () => {
      if (this.selectedCardId !== null) this.promptDestroy(this.selectedCardId);
    });
    return zone;
  }

  private buildGridColumn(grid: GridName): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'hex6-inv-editor__grid';
    el.dataset.grid = grid;
    return el;
  }

  private buildLoadoutColumn(): HTMLElement {
    const col = document.createElement('div');
    col.className = 'hex6-inv-editor__column hex6-inv-editor__column--loadout';

    const head = document.createElement('div');
    head.className = 'hex6-inv-editor__column-head';
    const kicker = document.createElement('span');
    kicker.className = 'hex6-inv-editor__kicker hex6-inv-editor__kicker--loadout';
    kicker.textContent = 'Loadout';
    const meta = document.createElement('span');
    meta.className = 'hex6-inv-editor__meta';
    meta.textContent = 'Active · 3×3';
    head.append(kicker, meta);

    const frozen = document.createElement('div');
    frozen.className = 'hex6-inv-editor__frozen';
    frozen.innerHTML = '<span class="hex6-inv-editor__frozen-dot"></span>Frozen during combat';

    const legend = this.buildDropStateLegend();

    if (this.loadoutMount === null) this.loadoutMount = this.buildGridColumn('loadout');
    col.append(head, frozen, this.loadoutMount, legend);
    return col;
  }

  /** The fits / blocked / R-rotate key for the loadout grid (mockup parity). */
  private buildDropStateLegend(): HTMLElement {
    const legend = document.createElement('div');
    legend.className = 'hex6-inv-editor__drop-legend';
    const entries: ReadonlyArray<[string, string]> = [
      ['fits', 'fits'],
      ['blocked', 'blocked'],
      ['rotate', 'R rotate'],
    ];
    for (const [kind, label] of entries) {
      const item = document.createElement('span');
      item.className = `hex6-inv-editor__drop-legend-item hex6-inv-editor__drop-legend-item--${kind}`;
      const swatch = document.createElement('span');
      swatch.className = 'hex6-inv-editor__drop-legend-swatch';
      item.append(swatch, document.createTextNode(label));
      legend.appendChild(item);
    }
    return legend;
  }

  private render(): void {
    if (this.loadoutMount) this.renderGrid(this.loadoutMount, 'loadout');
    if (this.offer) this.renderOfferPanel();
    else this.renderPreview();
  }

  private renderGrid(mount: HTMLDivElement, grid: GridName): void {
    const { placements, capacity } = this.inventory[grid];
    const selected = this.selectedCard();
    // Destination grid with the held card removed — the legality frame.
    const destination = placements.filter((p) => p.card.id !== this.selectedCardId);

    // Size the lattice to the loadout's capacity (3×3).
    mount.style.gridTemplateColumns = `repeat(${capacity.cols}, var(--inv-cell))`;
    mount.style.gridTemplateRows = `repeat(${capacity.rows}, var(--inv-cell))`;

    const children: HTMLElement[] = [];
    for (const { row, col } of allCells(capacity)) {
      const cell = document.createElement('div');
      cell.className = 'hex6-inv-editor__cell';
      cell.style.gridRow = String(row + 1);
      cell.style.gridColumn = String(col + 1);
      cell.dataset.row = String(row);
      cell.dataset.col = String(col);
      cell.dataset.grid = grid;
      if (
        selected !== null &&
        canPlace(destination, capacity, selected, { row, col }, this.pendingRotated)
      ) {
        cell.classList.add('hex6-inv-editor__cell--droppable');
      }
      cell.addEventListener('dragover', (e) => this.handleCellDragOver(e, cell, grid, row, col));
      cell.addEventListener('dragleave', () => this.clearCellHighlight(cell));
      cell.addEventListener('drop', (e) => this.handleCellDrop(e, grid, row, col));
      cell.addEventListener('click', () => this.handleCellClick(grid, row, col));
      children.push(cell);
    }
    for (const placement of placements) {
      children.push(this.renderCard(placement, grid));
    }
    mount.replaceChildren(...children);
  }

  private renderCard(placement: GridPlacement, grid: GridName): HTMLElement {
    const card = placement.card;
    // A packed die renders as the pip die-chip, never a card face (ADR-0024 §5);
    // every other card uses the unified PACKED face (HEX-103) that fills its grid
    // footprint (spine / glyph / gem / name matching the combat + shop card).
    const el = isDieCard(card)
      ? renderGridDieChip(card.dieType ?? PLAIN_D6)
      : renderCardFace(card, { mode: 'packed', rotated: placement.rotated });
    el.classList.add(
      'hex6-inv-editor__card',
      `hex6-inv-editor__card--${card.type}`,
      `hex6-inv-editor__card--${card.rarity}`,
    );
    el.setAttribute(DATA_CARD_ID, card.id);
    el.dataset.grid = grid;
    const area = cardGridArea(placement);
    el.style.gridRow = `${area.rowStart} / span ${area.rowSpan}`;
    el.style.gridColumn = `${area.colStart} / span ${area.colSpan}`;
    if (this.selectedCardId === card.id) el.classList.add('hex6-inv-editor__card--held');

    // The §19 extra-die scaling rule (HEX-97) is NOT drawn on the packed grid
    // face: a 1-row card (2×1) already stacks glyph + name + cost + foot into one
    // cell, and the extra descriptor line tipped the body past the cell's
    // overflow:hidden, clipping the foot (HEX-111). The rule still reads on hover
    // (the panel's §19 lines) and in the selected-card preview — packed mode shows
    // the at-a-glance essentials only (ADR-0020 §3 density rule).
    this.hoverPanel.bind(el, () => ({ card, rotated: placement.rotated }));

    el.draggable = true;
    el.addEventListener('dragstart', (e) => this.handleCardDragStart(e, card.id));
    el.addEventListener('dragend', () => this.handleCardDragEnd());
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.pickUp(card.id);
    });
    return el;
  }

  /** The selected-card preview (display-mode Card) + Equip auto-fit, or an
   * empty-state hint when nothing is held (ADR-0020 §4c). */
  private renderPreview(): void {
    if (!this.previewMount) return;
    const kicker = document.createElement('div');
    kicker.className = 'hex6-inv-editor__kicker hex6-inv-editor__kicker--preview';
    kicker.textContent = 'Selected';

    const selected = this.selectedCard();
    if (selected === null) {
      const empty = document.createElement('div');
      empty.className = 'hex6-inv-editor__preview-empty';
      empty.textContent = 'Pick a card to preview · drag to pack · R to rotate.';
      this.previewMount.replaceChildren(kicker, empty);
      return;
    }

    const panel = document.createElement('div');
    panel.className = 'hex6-inv-editor__preview-panel etch-deep';

    const face = renderCardFace(selected, {
      mode: 'display',
      rotated: this.pendingRotated,
      selected: true,
    });
    face.classList.add('hex6-inv-editor__preview-card');

    const fp = footprint(selected, this.pendingRotated);
    const meta = document.createElement('div');
    meta.className = 'hex6-inv-editor__preview-meta';
    meta.textContent =
      `${selected.type} · ${selected.rarity} · ${fp.cols}×${fp.rows}`.toUpperCase();

    const where = findPlacement(this.inventory, selected.id);
    const controls = document.createElement('div');
    controls.className = 'hex6-inv-editor__preview-controls';
    controls.append(
      this.buildPreviewButton(
        'Equip ▸',
        'equip',
        true,
        where?.grid === 'loadout' && !this.canFit('loadout'),
        () => this.placeHeldIn('loadout'),
      ),
      this.buildPreviewButton('Rotate (R)', 'rotate', false, false, () => this.toggleRotation()),
      this.buildPreviewButton('Cancel', 'cancel', false, false, () => {
        this.clearSelection();
        this.render();
      }),
    );

    panel.append(face, meta, controls);
    this.previewMount.replaceChildren(kicker, panel);
  }

  /** The incoming-offer panel (HEX-149) — shown in the rail beside the loadout.
   * Renders the incoming card/die, an Equip (first-fit) shortcut, Rotate, and a
   * Decline action. Equip is disabled while the loadout has no room, prompting the
   * player to destroy a card first. */
  private renderOfferPanel(): void {
    if (!this.previewMount || !this.offer) return;
    const offer = this.offer;
    const isDie = isDieCard(offer.item);

    const kicker = document.createElement('div');
    kicker.className = 'hex6-inv-editor__kicker hex6-inv-editor__kicker--preview';
    kicker.textContent = isDie ? 'New die' : 'New card';

    const panel = document.createElement('div');
    panel.className = 'hex6-inv-editor__preview-panel hex6-inv-editor__offer-panel etch-deep';
    panel.dataset.role = 'acq-offer';

    const face = isDie
      ? renderDieChip(offer.item.dieType ?? PLAIN_D6, { variant: 'offer' })
      : renderCardFace(offer.item, {
          mode: 'display',
          rotated: this.pendingRotated,
          selected: this.offerHeld,
        });
    face.classList.add('hex6-inv-editor__offer-card');
    face.dataset.role = 'acq-offer-card';
    face.draggable = true;
    face.addEventListener('dragstart', (e) => this.handleOfferDragStart(e));
    face.addEventListener('dragend', () => this.handleCardDragEnd());
    face.addEventListener('click', (e) => {
      e.stopPropagation();
      this.holdOffer();
    });

    const fp = footprint(offer.item, this.pendingRotated);
    const meta = document.createElement('div');
    meta.className = 'hex6-inv-editor__preview-meta';
    meta.textContent =
      `${offer.item.type} · ${offer.item.rarity} · ${fp.cols}×${fp.rows}`.toUpperCase();

    const fits = firstFitOnGrid(this.inventory.loadout, offer.item) !== null;
    const hint = document.createElement('div');
    hint.className = 'hex6-inv-editor__offer-hint';
    hint.dataset.role = 'acq-hint';
    hint.textContent = fits
      ? 'Drag it onto a cell, or Place to auto-fit.'
      : 'Your loadout is full — destroy a card to make room.';

    const controls = document.createElement('div');
    controls.className = 'hex6-inv-editor__preview-controls';
    controls.append(
      this.buildPreviewButton('Place ▸', 'acq-place', true, !fits, () => this.equipOffer()),
    );
    // A 1×1 die can't rotate meaningfully; only offer Rotate for larger cards.
    if (!isDie && offer.item.size.w !== offer.item.size.h) {
      controls.append(
        this.buildPreviewButton('Rotate (R)', 'acq-rotate', false, false, () =>
          this.toggleRotation(),
        ),
      );
    }
    controls.append(
      this.buildPreviewButton('Decline', 'acq-decline', false, false, () => this.declineOffer()),
    );

    panel.append(face, meta, hint, controls);
    this.previewMount.replaceChildren(kicker, panel);
  }

  private buildPreviewButton(
    label: string,
    action: string,
    primary: boolean,
    disabled: boolean,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `hex6-inv-editor__preview-btn btn${primary ? ' primary' : ''}`;
    btn.dataset.action = action;
    btn.textContent = label;
    btn.disabled = disabled;
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ── interaction ───────────────────────────────────────────────────────

  private pickUp(cardId: string): void {
    const found = findPlacement(this.inventory, cardId);
    if (found === null) return;
    // Grabbing a grid card releases the offer hold so the player can curate it
    // (move / destroy) before re-grabbing the offer to place it (HEX-149).
    this.offerHeld = false;
    if (this.selectedCardId === cardId) {
      this.clearSelection();
    } else {
      this.selectedCardId = cardId;
      this.pendingRotated = found.placement.rotated;
    }
    this.render();
  }

  /** Re-grab the incoming offer item so the next cell click/drop places it. */
  private holdOffer(): void {
    if (!this.offer) return;
    this.offerHeld = true;
    this.selectedCardId = null;
    this.pendingRotated = false;
    this.render();
  }

  private toggleRotation(): void {
    if (this.selectedCardId === null && !this.offerHeld) return;
    this.pendingRotated = !this.pendingRotated;
    this.render();
  }

  private placeAt(grid: GridName, row: number, col: number): void {
    if (this.selectedCardId === null) return;
    const next = moveCard(
      this.inventory,
      this.selectedCardId,
      grid,
      { row, col },
      this.pendingRotated,
    );
    if (next === null) {
      this.flashReject(grid, row, col);
      return;
    }
    this.inventory = next;
    this.clearSelection();
    this.onChange?.(this.inventory);
    this.render();
  }

  /** Whether the held card first-fits into `grid` with itself lifted out. */
  private canFit(grid: GridName): boolean {
    const card = this.selectedCard();
    if (card === null) return false;
    const lifted = {
      ...this.inventory[grid],
      placements: this.inventory[grid].placements.filter((p) => p.card.id !== this.selectedCardId),
    };
    return firstFitOnGrid(lifted, card) !== null;
  }

  /** Equip: first-fit the held card into the loadout (preview shortcut). No-op
   * (reject flash on the first cell) when it fits nowhere. */
  private placeHeldIn(grid: GridName): void {
    const card = this.selectedCard();
    if (card === null) return;
    const lifted = {
      ...this.inventory[grid],
      placements: this.inventory[grid].placements.filter((p) => p.card.id !== this.selectedCardId),
    };
    const fit = firstFitOnGrid(lifted, card);
    if (fit === null) {
      this.flashReject(grid, 0, 0);
      return;
    }
    this.pendingRotated = fit.rotated;
    this.placeAt(grid, fit.origin.row, fit.origin.col);
  }

  private handleCellClick(grid: GridName, row: number, col: number): void {
    if (this.offerHeld) {
      this.placeOffer(row, col);
      return;
    }
    if (this.selectedCardId === null) return;
    this.placeAt(grid, row, col);
  }

  /** Drag the offer chip — marks it held so the next cell drop places it. */
  private handleOfferDragStart(e: DragEvent): void {
    if (!this.offer) return;
    this.offerHeld = true;
    this.selectedCardId = null;
    this.dragActive = true;
    this.hoverPanel.hide();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', '__acq_offer__');
    }
    this.render();
  }

  /** Place the in-hand offer item at the chosen cell. A reject (no fit there)
   * keeps the item held so the player can curate more — never a silent loss. On
   * success the item is added and the acquisition resolves as `placed`. */
  private placeOffer(row: number, col: number): void {
    if (!this.offer) return;
    const result = placeCardAt(this.inventory, this.offer.item, { row, col }, this.pendingRotated);
    if (result === null) {
      this.flashReject('loadout', row, col);
      return;
    }
    this.inventory = result.inventory;
    this.offerHeld = false;
    this.onChange?.(this.inventory);
    this.resolveOffer('placed');
  }

  /** Equip the offer into the first free cell (the Place shortcut). No-op (reject
   * flash) when the loadout is full — the player destroys a card first. */
  private equipOffer(): void {
    if (!this.offer) return;
    const fit = firstFitOnGrid(this.inventory.loadout, this.offer.item);
    if (fit === null) {
      this.flashReject('loadout', 0, 0);
      return;
    }
    this.pendingRotated = fit.rotated;
    this.placeOffer(fit.origin.row, fit.origin.col);
  }

  /** Forgo the offer (back-out / Decline). Resolves as `declined`. */
  private declineOffer(): void {
    this.resolveOffer('declined');
  }

  /** Resolve the offer exactly once and hand the outcome to the host. */
  private resolveOffer(outcome: 'placed' | 'declined'): void {
    if (this.offerSettled || !this.offer) return;
    this.offerSettled = true;
    const cb = this.offer.onResolve;
    this.offer = null;
    this.offerHeld = false;
    cb(outcome);
  }

  private handleCardDragStart(e: DragEvent, cardId: string): void {
    const found = findPlacement(this.inventory, cardId);
    if (found === null) return;
    this.selectedCardId = cardId;
    this.pendingRotated = found.placement.rotated;
    this.dragActive = true;
    this.hoverPanel.hide();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(DRAG_MIME, cardId);
      e.dataTransfer.setData('text/plain', cardId);
    }
    this.render();
  }

  /** A drag that ended without a committed cell drop (off-grid) clears the held
   * card so the editor doesn't get stuck holding it. `placeAt` resets
   * `dragActive` on a successful drop, so this only fires for cancelled drags. */
  private handleCardDragEnd(): void {
    if (!this.dragActive) return;
    this.dragActive = false;
    if (this.selectedCardId !== null) {
      this.clearSelection();
      this.render();
    }
  }

  private handleCellDragOver(
    e: DragEvent,
    cell: HTMLElement,
    grid: GridName,
    row: number,
    col: number,
  ): void {
    if (this.selectedCardId === null && !this.offerHeld) return;
    const card = this.selectedCard();
    if (card === null) return;
    const { placements, capacity } = this.inventory[grid];
    const destination = placements.filter((p) => p.card.id !== this.selectedCardId);
    if (canPlace(destination, capacity, card, { row, col }, this.pendingRotated)) {
      e.preventDefault();
      cell.classList.add('hex6-inv-editor__cell--drop-ok');
      cell.classList.remove('hex6-inv-editor__cell--drop-no');
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    } else {
      // Blocked target — still preventDefault so the cursor reflects "no drop"
      // here rather than the browser's default copy affordance.
      e.preventDefault();
      cell.classList.add('hex6-inv-editor__cell--drop-no');
      cell.classList.remove('hex6-inv-editor__cell--drop-ok');
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    }
  }

  private clearCellHighlight(cell: HTMLElement): void {
    cell.classList.remove('hex6-inv-editor__cell--drop-ok');
    cell.classList.remove('hex6-inv-editor__cell--drop-no');
  }

  private handleCellDrop(e: DragEvent, grid: GridName, row: number, col: number): void {
    e.preventDefault();
    this.dragActive = false;
    if (this.offerHeld) {
      this.placeOffer(row, col);
      return;
    }
    const cardId = readDragCardId(e) ?? this.selectedCardId;
    if (cardId === null) return;
    if (cardId !== this.selectedCardId) {
      const found = findPlacement(this.inventory, cardId);
      if (found === null) return;
      this.selectedCardId = cardId;
      this.pendingRotated = found.placement.rotated;
    }
    this.placeAt(grid, row, col);
  }

  private handleDestroyDragOver(e: DragEvent, zone: HTMLElement): void {
    if (this.selectedCardId === null) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    zone.classList.add('hex6-inv-editor__destroy--over');
  }

  private handleDestroyDrop(e: DragEvent, zone: HTMLElement): void {
    e.preventDefault();
    this.dragActive = false;
    zone.classList.remove('hex6-inv-editor__destroy--over');
    const cardId = readDragCardId(e) ?? this.selectedCardId;
    if (cardId === null) return;
    if (findPlacement(this.inventory, cardId) === null) return;
    this.promptDestroy(cardId);
  }

  /** Confirm overlay before a permanent removal (HEX-147). Destroy → `removeCard`
   * + onChange; Cancel → leave the card in place. */
  private promptDestroy(cardId: string): void {
    if (!this.root) return;
    const card = findPlacement(this.inventory, cardId)?.placement.card;
    if (!card) return;

    const overlay = document.createElement('div');
    overlay.className = 'hex6-inv-editor__confirm';
    overlay.dataset.role = 'destroy-confirm';
    overlay.setAttribute('role', 'alertdialog');

    const panel = document.createElement('div');
    panel.className = 'hex6-inv-editor__confirm-panel etch-deep';
    const prompt = document.createElement('p');
    prompt.className = 'hex6-inv-editor__confirm-prompt';
    prompt.textContent = `Destroy ${card.name}? This can't be undone.`;

    const controls = document.createElement('div');
    controls.className = 'hex6-inv-editor__confirm-controls';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'hex6-inv-editor__confirm-btn btn';
    cancel.dataset.action = 'destroy-cancel';
    cancel.textContent = 'Cancel';
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'hex6-inv-editor__confirm-btn hex6-inv-editor__confirm-btn--danger btn';
    confirm.dataset.action = 'destroy-confirm';
    confirm.textContent = 'Destroy';

    const close = () => overlay.remove();
    cancel.addEventListener('click', close);
    confirm.addEventListener('click', () => {
      close();
      this.destroyCard(cardId);
    });

    controls.append(cancel, confirm);
    panel.append(prompt, controls);
    overlay.appendChild(panel);
    this.root.appendChild(overlay);
  }

  private destroyCard(cardId: string): void {
    const next = removeCard(this.inventory, cardId);
    if (next === null) return;
    this.inventory = next;
    this.clearSelection();
    this.onChange?.(this.inventory);
    this.render();
  }

  private onKey(e: KeyboardEvent): void {
    if ((e.key === 'r' || e.key === 'R') && (this.selectedCardId !== null || this.offerHeld)) {
      this.toggleRotation();
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      if (this.offer) {
        this.declineOffer();
        e.preventDefault();
      } else if (this.selectedCardId !== null) {
        this.clearSelection();
        this.render();
        e.preventDefault();
      } else {
        this.onClose?.();
      }
    }
  }

  // ── helpers ───────────────────────────────────────────────────────────

  private selectedCard(): Card | null {
    if (this.offerHeld && this.offer) return this.offer.item;
    if (this.selectedCardId === null) return null;
    return findPlacement(this.inventory, this.selectedCardId)?.placement.card ?? null;
  }

  private clearSelection(): void {
    this.selectedCardId = null;
    this.pendingRotated = false;
  }

  private flashReject(_grid: GridName, row: number, col: number): void {
    const cell = this.loadoutMount?.querySelector<HTMLElement>(
      `.hex6-inv-editor__cell[data-row="${row}"][data-col="${col}"]`,
    );
    if (!cell) return;
    cell.classList.remove('hex6-inv-editor__cell--reject');
    void cell.offsetWidth;
    cell.classList.add('hex6-inv-editor__cell--reject');
    setTimeout(() => cell.classList.remove('hex6-inv-editor__cell--reject'), 240);
  }
}

function readDragCardId(e: DragEvent): string | null {
  const dt = e.dataTransfer;
  if (!dt) return null;
  let raw = '';
  try {
    raw = dt.getData(DRAG_MIME);
  } catch {
    raw = '';
  }
  if (raw === '') {
    try {
      raw = dt.getData('text/plain');
    } catch {
      raw = '';
    }
  }
  return raw === '' ? null : raw;
}
